import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ClipboardCheck, 
  Ruler, 
  Home, 
  Paintbrush, 
  Zap, 
  Wrench,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  AlertCircle,
  RotateCcw,
  Loader2
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// PRE-ANALYSIS QUESTIONNAIRE COMPONENT
// Collects project specifications before generating material takeoff
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_ANSWERS = {
  stories: "1",
  ceilingHeight: "9",
  exteriorWallType: "2x6",
  interiorWallType: "2x4",
  finishLevel: "standard",
  electricalScope: "full",
  plumbingScope: "full",
  hvacScope: "full",
  garageSize: "2-car",
  hasBasement: "slab",
  roofType: "asphalt_shingle",
  roofPitch: "4/12",
  additionalNotes: ""
};

const PreAnalysisQuestionnaire = ({ 
  onComplete, 
  onSkip, 
  onClose,
  blueprintFilename 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [hasUserDefaults, setHasUserDefaults] = useState(false);

  // Load user defaults on mount
  useEffect(() => {
    loadDefaults();
  }, []);

  const loadDefaults = async () => {
    setLoadingDefaults(true);
    try {
      const response = await API.get("/blueprint-takeoff/questionnaire/defaults");
      if (response.data?.defaults) {
        setAnswers(prev => ({
          ...prev,
          ...response.data.defaults
        }));
        setHasUserDefaults(response.data.source === "user");
      }
    } catch (err) {
      console.log("Using system defaults");
    } finally {
      setLoadingDefaults(false);
    }
  };

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await API.post("/blueprint-takeoff/questionnaire/defaults", answers);
      toast.success("Defaults saved! They'll auto-fill on future uploads.");
      setHasUserDefaults(true);
    } catch (err) {
      toast.error("Failed to save defaults");
      console.error(err);
    } finally {
      setSavingDefaults(false);
    }
  };

  const resetDefaults = async () => {
    try {
      await API.delete("/blueprint-takeoff/questionnaire/defaults");
      setAnswers(DEFAULT_ANSWERS);
      setHasUserDefaults(false);
      toast.success("Defaults reset to system values");
    } catch (err) {
      toast.error("Failed to reset defaults");
    }
  };

  const updateAnswer = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const steps = [
    {
      title: "Building Dimensions",
      icon: <Ruler className="h-5 w-5" />,
      description: "Help us understand the scale of the project",
      fields: [
        {
          id: "stories",
          label: "Number of Stories",
          type: "select",
          options: [
            { value: "1", label: "1 Story" },
            { value: "1.5", label: "1.5 Stories (Bonus Room)" },
            { value: "2", label: "2 Stories" },
            { value: "3", label: "3+ Stories" }
          ]
        },
        {
          id: "ceilingHeight",
          label: "Main Floor Ceiling Height",
          type: "select",
          options: [
            { value: "8", label: "8 ft (Standard)" },
            { value: "9", label: "9 ft" },
            { value: "10", label: "10 ft" },
            { value: "12", label: "12 ft (Cathedral)" },
            { value: "varies", label: "Varies by Room" }
          ]
        }
      ]
    },
    {
      title: "Wall Construction",
      icon: <Home className="h-5 w-5" />,
      description: "Framing specifications for walls",
      fields: [
        {
          id: "exteriorWallType",
          label: "Exterior Wall Framing",
          type: "select",
          options: [
            { value: "2x4", label: "2x4 (Standard)" },
            { value: "2x6", label: "2x6 (Energy Code / Cold Climate)" },
            { value: "2x8", label: "2x8 (High Performance)" },
            { value: "icf", label: "ICF (Insulated Concrete Forms)" },
            { value: "sip", label: "SIP (Structural Insulated Panels)" }
          ]
        },
        {
          id: "interiorWallType",
          label: "Interior Wall Framing",
          type: "select",
          options: [
            { value: "2x4", label: "2x4 (Standard)" },
            { value: "2x6", label: "2x6 (Plumbing Walls)" },
            { value: "steel", label: "Steel Studs" }
          ]
        }
      ]
    },
    {
      title: "Finish Level",
      icon: <Paintbrush className="h-5 w-5" />,
      description: "Overall quality grade for finishes",
      fields: [
        {
          id: "finishLevel",
          label: "Finish Quality Level",
          type: "select",
          options: [
            { value: "builder", label: "Builder Grade - Basic fixtures, vinyl flooring, laminate counters" },
            { value: "standard", label: "Standard - Mid-grade fixtures, LVP/carpet, granite counters" },
            { value: "upgraded", label: "Upgraded - Quality fixtures, hardwood/tile, quartz counters" },
            { value: "custom", label: "Custom/Luxury - Premium fixtures, custom finishes throughout" }
          ],
          fullWidth: true
        }
      ]
    },
    {
      title: "MEP Scope",
      icon: <Zap className="h-5 w-5" />,
      description: "Mechanical, Electrical & Plumbing specifications",
      fields: [
        {
          id: "electricalScope",
          label: "Electrical Scope",
          type: "select",
          options: [
            { value: "full", label: "Full - Complete new electrical system" },
            { value: "rough", label: "Rough Only - Wiring, no fixtures" },
            { value: "upgrade", label: "Panel Upgrade + Circuits" },
            { value: "by_others", label: "By Others / Not Included" }
          ]
        },
        {
          id: "plumbingScope",
          label: "Plumbing Scope",
          type: "select",
          options: [
            { value: "full", label: "Full - Complete new plumbing system" },
            { value: "rough", label: "Rough Only - Pipes, no fixtures" },
            { value: "fixtures", label: "Fixtures Only (Tie to existing)" },
            { value: "by_others", label: "By Others / Not Included" }
          ]
        },
        {
          id: "hvacScope",
          label: "HVAC Scope",
          type: "select",
          options: [
            { value: "full", label: "Full - New furnace, AC, ductwork" },
            { value: "ductwork", label: "Ductwork Only (Equipment by others)" },
            { value: "mini_split", label: "Mini-Split System" },
            { value: "by_others", label: "By Others / Not Included" }
          ]
        }
      ]
    },
    {
      title: "Special Considerations",
      icon: <Wrench className="h-5 w-5" />,
      description: "Additional features that affect materials",
      fields: [
        {
          id: "garageSize",
          label: "Garage",
          type: "select",
          options: [
            { value: "none", label: "No Garage" },
            { value: "1-car", label: "1-Car Attached" },
            { value: "2-car", label: "2-Car Attached" },
            { value: "3-car", label: "3-Car Attached" },
            { value: "detached", label: "Detached Garage" }
          ]
        },
        {
          id: "hasBasement",
          label: "Basement / Foundation Type",
          type: "select",
          options: [
            { value: "slab", label: "Slab on Grade" },
            { value: "crawl", label: "Crawl Space" },
            { value: "unfinished", label: "Unfinished Basement" },
            { value: "finished", label: "Finished Basement" }
          ]
        },
        {
          id: "roofType",
          label: "Roofing Material",
          type: "select",
          options: [
            { value: "asphalt_shingle", label: "Asphalt Architectural Shingles" },
            { value: "3tab", label: "3-Tab Shingles (Budget)" },
            { value: "metal_29ga", label: "29 Gauge Metal Panels ($3.00/LF)" },
            { value: "metal_26ga", label: "26 Gauge Metal Panels ($3.75/LF)" },
            { value: "metal_standing", label: "Standing Seam Metal" },
            { value: "tile", label: "Concrete/Clay Tile" },
            { value: "tpo", label: "TPO/Flat Roof" }
          ]
        },
        {
          id: "roofPitch",
          label: "Roof Pitch",
          type: "select",
          options: [
            { value: "2/12", label: "2/12 (Low Slope)" },
            { value: "3/12", label: "3/12 (Low Slope)" },
            { value: "4/12", label: "4/12 (Standard)" },
            { value: "5/12", label: "5/12 (Standard)" },
            { value: "6/12", label: "6/12 (Standard)" },
            { value: "8/12", label: "8/12 (Steep)" },
            { value: "10/12", label: "10/12 (Steep)" },
            { value: "12/12", label: "12/12 (Very Steep - 45°)" }
          ]
        }
      ]
    },
    {
      title: "Additional Notes",
      icon: <ClipboardCheck className="h-5 w-5" />,
      description: "Any other details that affect the takeoff",
      fields: [
        {
          id: "additionalNotes",
          label: "Special Instructions or Exclusions",
          type: "textarea",
          placeholder: "e.g., 'Owner providing appliances', 'Exclude landscaping', 'Include covered porch 12x16'",
          fullWidth: true
        }
      ]
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      // Save defaults if checkbox is checked
      if (saveAsDefault) {
        saveDefaults();
      }
      onComplete(answers);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canProceed = () => {
    const currentFields = currentStepData.fields;
    for (const field of currentFields) {
      if (field.required && !answers[field.id]) {
        return false;
      }
    }
    return true;
  };

  if (loadingDefaults) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="bg-[#0f172a] border-2 border-emerald-500/30 rounded-xl p-8 flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
          <p className="text-sm font-mono text-slate-400">Loading your preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#0f172a] border-2 border-emerald-500/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl shadow-emerald-500/10">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-[#0f172a] to-[#1e293b] border-b border-emerald-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <ClipboardCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-mono font-bold text-emerald-400 uppercase tracking-wider">
                Pre-Analysis Questionnaire
              </h2>
              <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                {blueprintFilename || "Blueprint Analysis"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Saved Defaults Indicator */}
            {hasUserDefaults && (
              <span className="text-[10px] font-mono text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded">
                Using Your Defaults
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-800/50 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-3 bg-slate-900/50 border-b border-slate-800/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono text-slate-400">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-xs font-mono text-emerald-400">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="flex gap-1">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  idx <= currentStep ? 'bg-emerald-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Step Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              {currentStepData.icon}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {currentStepData.title}
              </h3>
              <p className="text-sm text-slate-400">
                {currentStepData.description}
              </p>
            </div>
          </div>

          {/* Fields */}
          <div className={`grid gap-4 ${currentStepData.fields.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {currentStepData.fields.map((field) => (
              <div 
                key={field.id} 
                className={field.fullWidth ? 'md:col-span-2' : ''}
              >
                <Label 
                  htmlFor={field.id}
                  className="text-sm font-mono text-slate-300 mb-2 block"
                >
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                
                {field.type === "number" && (
                  <Input
                    id={field.id}
                    type="number"
                    placeholder={field.placeholder}
                    value={answers[field.id]}
                    onChange={(e) => updateAnswer(field.id, e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white font-mono focus:border-emerald-500"
                    data-testid={`questionnaire-${field.id}`}
                  />
                )}
                
                {field.type === "text" && (
                  <Input
                    id={field.id}
                    type="text"
                    placeholder={field.placeholder}
                    value={answers[field.id]}
                    onChange={(e) => updateAnswer(field.id, e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white font-mono focus:border-emerald-500"
                    data-testid={`questionnaire-${field.id}`}
                  />
                )}
                
                {field.type === "select" && (
                  <Select 
                    value={answers[field.id]} 
                    onValueChange={(value) => updateAnswer(field.id, value)}
                  >
                    <SelectTrigger 
                      className="bg-slate-800/50 border-slate-700 text-white font-mono focus:border-emerald-500"
                      data-testid={`questionnaire-${field.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent 
                      className="bg-slate-800 border-slate-700 z-[100]"
                      position="popper"
                      sideOffset={4}
                    >
                      {field.options.map((opt) => (
                        <SelectItem 
                          key={opt.value} 
                          value={opt.value}
                          className="text-white font-mono text-xs hover:bg-slate-700 focus:bg-slate-700 cursor-pointer"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {field.type === "textarea" && (
                  <Textarea
                    id={field.id}
                    placeholder={field.placeholder}
                    value={answers[field.id]}
                    onChange={(e) => updateAnswer(field.id, e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white font-mono focus:border-emerald-500 min-h-[100px]"
                    data-testid={`questionnaire-${field.id}`}
                  />
                )}
                
                {field.helperText && (
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">
                    {field.helperText}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Save as Default Checkbox - only on last step */}
          {isLastStep && (
            <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="saveAsDefault"
                  checked={saveAsDefault}
                  onCheckedChange={setSaveAsDefault}
                  className="border-emerald-500 data-[state=checked]:bg-emerald-600"
                  data-testid="save-as-default-checkbox"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="saveAsDefault"
                    className="text-sm font-mono text-slate-300 cursor-pointer"
                  >
                    Save as my defaults
                  </Label>
                  <p className="text-[10px] text-slate-500 font-mono">
                    Auto-fill these values on future blueprint uploads
                  </p>
                </div>
                {hasUserDefaults && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetDefaults}
                    className="text-slate-500 hover:text-red-400 font-mono text-xs"
                    data-testid="reset-defaults-btn"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-900/50 border-t border-slate-800/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-slate-400 hover:text-white font-mono"
                data-testid="questionnaire-back-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-slate-500 hover:text-slate-300 font-mono text-xs"
              data-testid="questionnaire-skip-btn"
            >
              <AlertCircle className="h-3 w-3 mr-1" />
              Skip & Use Defaults
            </Button>
            
            <Button
              size="sm"
              onClick={handleNext}
              disabled={!canProceed() || savingDefaults}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-mono shadow-lg shadow-emerald-900/30"
              data-testid="questionnaire-next-btn"
            >
              {savingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : isLastStep ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Generate Takeoff
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreAnalysisQuestionnaire;
